import React from "react";
import { graphql, useFragment } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { faCaretDown } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";

import { DismissibleElementsContext } from "~/common/DismissibleElements";
import { ConnectedIcon } from "~/stream/ConnectedIcon";
import { AccountAvatar, Button, Tooltip } from "~/ui";
import { HelpCenterLink } from "~/ui/legacyFellowIndex";
import Menu from "~/ui/menu";

import type { MeetingTitleAndXWCSwitcher_ExternalStreamTooltip_stream$key } from "~/__generated__/MeetingTitleAndXWCSwitcher_ExternalStreamTooltip_stream.graphql";
import type { MeetingTitleAndXWCSwitcher_stream$key } from "~/__generated__/MeetingTitleAndXWCSwitcher_stream.graphql";
import type { MeetingTitleAndXWCSwitcher_viewer$key } from "~/__generated__/MeetingTitleAndXWCSwitcher_viewer.graphql";
import type { MeetingTitleAndXWCSwitcherOption_stream$key } from "~/__generated__/MeetingTitleAndXWCSwitcherOption_stream.graphql";

import styles from "./MeetingTitleAndXWCSwitcher.module.css";

export function MeetingTitle({ meetingTitle }: { meetingTitle: string }) {
	return (
		<span className={styles.meetingTitle} title={meetingTitle}>
			{meetingTitle}
		</span>
	);
}

function WorkspaceOption({
	stream: streamKey,
	selected,
	accountOccurrenceNumber,
}: {
	stream: MeetingTitleAndXWCSwitcherOption_stream$key;
	selected?: boolean;
	accountOccurrenceNumber: number;
}) {
	const stream = useFragment(
		graphql`
			fragment MeetingTitleAndXWCSwitcherOption_stream on NoteStream {
				id
				displayName
				url
				account {
					id
					logo
					name
				}
			}
		`,
		streamKey,
	);

	if (!stream.url) return null;

	let workspaceName = stream.account.name;
	if (accountOccurrenceNumber > 1) workspaceName += ` (${accountOccurrenceNumber})`;

	return (
		<Menu.ItemLink
			account={{
				name: workspaceName,
				image: stream.account.logo,
			}}
			to={stream.url}
			className={cx({ [styles.selectedOption]: selected })}
		>
			{workspaceName}
			{selected && <FontAwesomeIcon icon={faCheck} className={styles.selectedCheck} />}
		</Menu.ItemLink>
	);
}

export default function MeetingTitleAndXWCSwitcher({
	stream: streamKey,
	viewer: viewerKey,
	meetingTitle,
}: {
	stream: MeetingTitleAndXWCSwitcher_stream$key;
	viewer: MeetingTitleAndXWCSwitcher_viewer$key;
	meetingTitle?: string;
}) {
	const viewer = useFragment(
		graphql`
			fragment MeetingTitleAndXWCSwitcher_viewer on Viewer {
				account {
					id
				}
			}
		`,
		viewerKey,
	);

	const stream = useFragment(
		graphql`
			fragment MeetingTitleAndXWCSwitcher_stream on NoteStream {
				isExternal
				account {
					id
					image: logo
					name
				}

				...MeetingTitleAndXWCSwitcher_ExternalStreamTooltip_stream
				...MeetingTitleAndXWCSwitcherOption_stream

				relatedStreams {
					id
					isExternal
					account {
						id
						name
					}
					...MeetingTitleAndXWCSwitcherOption_stream
				}
			}
		`,
		streamKey,
	);

	const [open, setOpen] = React.useState(false);
	const onOpenChange = React.useCallback((open: boolean) => {
		setOpen(open);
	}, []);

	const tooltipReference = React.useRef<HTMLButtonElement>(null);
	const meetingTitleElement = meetingTitle ? (
		<span className={styles.meetingTitle} title={meetingTitle}>
			{meetingTitle}
		</span>
	) : null;

	if (!stream.relatedStreams.length && stream.account.id === viewer.account?.id) return meetingTitleElement;

	const accountLogo = <AccountAvatar account={stream.account} className={styles.accountAvatar} size="small" />;

	if (!stream.isExternal && !stream.relatedStreams.some(s => s.isExternal)) {
		return meetingTitleElement;
	} else if (stream.relatedStreams.length === 0) {
		return (
			<div className={styles.meetingTitleWithAccountAvatar}>
				{accountLogo}
				{meetingTitleElement}
			</div>
		);
	}

	const seenAccounts = { [stream.account.id]: 2 };
	return (
		<ExternalStreamTooltip stream={stream} reference={tooltipReference}>
			<span>
				<Menu open={open} onOpenChange={onOpenChange}>
					<Menu.Trigger asChild>
						<Button ref={tooltipReference} variant="tertiary" size="slim" className={styles.trigger}>
							{accountLogo}
							{meetingTitleElement}
							<FontAwesomeIcon icon={faCaretDown} className={styles.triggerIcon} />
						</Button>
					</Menu.Trigger>
					<Menu.Content>
						<WorkspaceOption stream={stream} selected accountOccurrenceNumber={1} />
						{stream.relatedStreams.map(stream => {
							const accountOccurrence = (seenAccounts[stream.account.id] ??= 1);
							seenAccounts[stream.account.id] += 1;
							return <WorkspaceOption key={stream.id} stream={stream} accountOccurrenceNumber={accountOccurrence} />;
						})}
					</Menu.Content>
				</Menu>
			</span>
		</ExternalStreamTooltip>
	);
}

const CROSS_WORKSPACE_POPUP_KEY = "StreamSwitcher__crossWorkspacePopup";

interface ExternalStreamTooltipProps extends Pick<React.ComponentProps<typeof Tooltip>, "children" | "reference"> {
	stream: MeetingTitleAndXWCSwitcher_ExternalStreamTooltip_stream$key;
}

function ExternalStreamTooltip({ children, reference, stream: streamKey }: ExternalStreamTooltipProps) {
	const stream = useFragment(
		graphql`
			fragment MeetingTitleAndXWCSwitcher_ExternalStreamTooltip_stream on NoteStream {
				id
				isExternal
				account {
					name
				}
				relatedStreams {
					id
					isExternal
					account {
						name
					}
				}
			}
		`,
		streamKey,
	);

	const { hasDismissed, dismiss } = React.useContext(DismissibleElementsContext);

	const dismissCrossWorkspacePopup = React.useCallback(() => {
		dismiss(CROSS_WORKSPACE_POPUP_KEY);
	}, [dismiss]);

	const externalAccount = stream.isExternal
		? stream.account
		: stream.relatedStreams.find(stream => stream.isExternal)?.account;

	// There should always be an external account, otherwise this component would
	// not be rendered, but we'll handle this case since the types allow it.
	if (!externalAccount) return <>{children}</>;

	return (
		<Tooltip
			visible={!hasDismissed(CROSS_WORKSPACE_POPUP_KEY)}
			appendTo={document.documentElement}
			reference={reference}
			interactive
			className={styles.tooltip}
			placement="bottom-start"
			content={
				<article>
					<header className={styles.tooltipHeader}>
						<h1>
							<ConnectedIcon inverted className={styles.tooltipConnectedIcon} />
							You&apos;re collaborating with {externalAccount.name}
						</h1>
					</header>
					<main className={styles.tooltipBody}>
						<p>
							This note was shared with you from someone outside your workspace. You can switch between the shared note
							and your internal note via this dropdown.{" "}
							<HelpCenterLink articleId="6782177" className={styles.learnMoreLink}>
								Learn more
							</HelpCenterLink>
						</p>
					</main>
					<footer className={styles.tooltipFooter}>
						<Button variant="primary" onClick={dismissCrossWorkspacePopup}>
							Got it
						</Button>
					</footer>
				</article>
			}
		>
			{children}
		</Tooltip>
	);
}
